import dayjs, { parseZone } from 'utils/dayjs';
import { LAST_30_DAYS, LAST_3_MONTHS, LAST_7_DAYS, LAST_MONTH, LAST_YEAR_1M, TODAY } from './AnalyticsConstants';
import { ChartData, TApiResponse, TCombinedChartData, TGraphData } from "./analytics";

const generateCombinedData = (chartData: ChartData[]): TCombinedChartData[] => {
  return chartData.map((item) => {
    const columnData = item.data.map((point) => ({ x: point.x, y: point.y, }));
    const lineData = item.data.map((point) => ({ x: point.x, y: point.y, }));
    return { name: item.name, columnData: columnData, lineData: lineData, };
  });
}

export function formatDateToLexicographicalOrder(date: any, timezone: string) {
  const adjustedDate = dayjs.tz(date, timezone);

  const weekday = adjustedDate.format('ddd');
  const month = adjustedDate.format('MMM');
  const day = adjustedDate.format('D');

  return `${weekday}, ${month} ${day}`;
}

export function formatCustomDate(date: any) {
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dateInOriginalTimezone = parseZone(date);
  const day = daysOfWeek[dateInOriginalTimezone.day()];
  const hours = dateInOriginalTimezone.format('H');

  return `${day}, ${hours}H`;
}

export const convertApiResponseToGraph = (apiResponse: TApiResponse, granularity: string): TGraphData[] => {
  const graphData: TGraphData[] = [];
  for (const metricName in apiResponse) {
    const metric_name = metricName;
    const data = apiResponse[metric_name];
    const chartData: ChartData[] = [];
    const applicationNames: string[] = [];
    const allDates = new Set<string>();
    for (const dataset of data as any) {
      let value = 0;
      value = parseFloat(dataset?.metric_value === 'Infinity' ? '0' : dataset.metric_value === undefined ? '0' : dataset?.metric_value?.toString() ?? '0');
      const appName = dataset.application_name || 'ARS';
      let dataIndex = applicationNames.indexOf(appName);
      if (dataIndex === -1) {
        applicationNames.push(appName);
        chartData.push({ name: appName, data: [] });
        dataIndex = chartData.length - 1;
      }
      if (!isNaN(value) && isFinite(value)) {
        const dateString = dataset.time_interval;
        allDates.add(dateString);
        chartData[dataIndex].data.push({ x: dateString, y: parseFloat(value.toFixed(3)) });
      }
    }
    const combinedData = generateCombinedData(chartData);
    let dashedArray: number[] = [];
    const chartDataLength = chartData.length;
    if (chartDataLength !== 0) {
      const result = chartDataLength / 2;
      dashedArray = calculateDashArray(true, result, 0);
    }
    const uniqueDates = Array.from(allDates);
    graphData.push({ dashedArray: dashedArray, metric_name: metric_name, chartData: chartData, combinedData: combinedData, xAxis: uniqueDates });
  }
  return graphData;
};

export const combineApiResponseToGraph = (currentApiResponse: TApiResponse, previousApiResponse: TApiResponse, granularity: string): TGraphData[] => {
  const metrics: TGraphData[] = [];

  for (const metricName in currentApiResponse) {
    const currentData = currentApiResponse[metricName];
    const previousData = previousApiResponse[metricName] || [];
    const metric_name = metricName;

    const chartData: ChartData[] = [];
    const allDates = new Set<string>();
    const combinedData = combineDataByApplication(currentData as any, previousData as any, metric_name);

    for (const dataset of combinedData) {
      let value = 0;
      value = parseFloat(dataset?.metric_value === 'Infinity' ? '0' : dataset?.metric_value === undefined ? '0' : dataset?.metric_value?.toString() ?? '0');
      const appName = dataset.application_name || 'ARS';

      if (!isNaN(value) && isFinite(value)) {
        const date = new Date(dataset.Time_Interval || dataset.time_interval);
        allDates.add(dataset.Time_Interval || dataset.time_interval);
        let chartEntry = chartData.find(entry => entry.name === appName);
        if (!chartEntry) {
          chartEntry = { name: appName, data: [] };
          chartData.push(chartEntry);
        }
        chartEntry.data.push({ x: date ?? new Date(), y: parseFloat(value.toFixed(3)) });
      }
    }
    let dashedArray: number[] = [];
    const chartDataLength = chartData.length;
    if (chartDataLength !== 0) {
      const result = chartDataLength / 2;
      dashedArray = calculateDashArray(true, result, result);
    } else {
      console.error("Division by zero error!");
    }

    const uniqueDates = Array.from(allDates);

    metrics.push({
      dashedArray: dashedArray,
      metric_name: metric_name,
      chartData: chartData,
      combinedData: generateCombinedData(chartData),
      xAxis: uniqueDates,
    });
  }

  return metrics;
}

const combineDataByApplication = (currentData: any[], previousData: any[], metricName: string): any[] => {
  const combinedData: any[] = [];
  for (const dataset of currentData) {
    combinedData.push(dataset);
  }
  for (const dataset of previousData) {
    const appName = dataset.application_name || 'ARS';
    dataset.application_name = `${appName} (PP)`;
    combinedData.push(dataset);
  }
  return combinedData;
}

export function calculatePreviousPeriod(dateRange: string[], granularity: string, rangeLabel: string, timeZone: string): [string, string] {
  const startMoment = dayjs.tz(dateRange[0], timeZone);
  const endMoment = dayjs.tz(dateRange[1], timeZone);

  let previousStart: dayjs.Dayjs;
  let previousEnd: dayjs.Dayjs;

  switch (rangeLabel) {
    case TODAY:
      previousStart = startMoment.clone().subtract(1, 'day');
      previousEnd = endMoment.clone().subtract(1, 'day');
      break;
    case LAST_7_DAYS:
      previousStart = startMoment.clone().subtract(7, 'days');
      previousEnd = endMoment.clone().subtract(7, 'days');
      break;
    case LAST_30_DAYS:
      previousStart = startMoment.clone().subtract(30, 'days');
      previousEnd = endMoment.clone().subtract(30, 'days');
      break;
    case LAST_MONTH:
      previousStart = startMoment.clone().subtract(1, 'month');
      previousEnd = endMoment.clone().subtract(1, 'month');
      break;
    case LAST_3_MONTHS:
      previousStart = startMoment.clone().subtract(3, 'months');
      previousEnd = endMoment.clone().subtract(3, 'months');
      break;
    case LAST_YEAR_1M:
      previousStart = startMoment.clone().subtract(1, 'year');
      previousEnd = endMoment.clone().subtract(1, 'year');
      break;
    default:
      // Fallback: subtract the duration for custom ranges
      const duration = endMoment.diff(startMoment);
      previousStart = startMoment.clone().subtract(duration, 'milliseconds');
      previousEnd = endMoment.clone().subtract(duration, 'milliseconds');
  }

  return [
    previousStart.format('YYYY-MM-DD HH:mm:ss'),
    previousEnd.format('YYYY-MM-DD HH:mm:ss')
  ];
}

export const calculateDashArray = (isPreviousData: boolean, currentDataSize: number, previousDataSize: number): number[] => {
  const dashArray: number[] = [];
  for (let i = 0; i < currentDataSize; i++) {
    dashArray.push(0);
  }
  if (isPreviousData) {
    for (let i = 0; i < previousDataSize; i++) {
      dashArray.push(4);
    }
  }
  return dashArray;
}

export const formatCurrency = (number: number, locale: string = 'en-US', currencyCode: string = 'USD'): string => {
  if (isNaN(number) || !isFinite(number)) {
    return 'Invalid Number';
  }
  return number.toLocaleString(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });
}

export const updateYAxisTitle = (metricName: string): string => {
  if (["add_to_cart_rate", "cart_conv_rate", "order_conversion_rate"].includes(metricName)) {
    return "The values are in %";
  }
  if (metricName === "order_value") {
    return "The values are in $";
  }
  return "";
};

export const areAllChartDataAndCombinedDataEmpty = (data: any[]): boolean => {
  if (data.length === 0) return true;
  return data.every((item) => item.chartData.length === 0 && item.combinedData.length === 0);
}

export const isMetricDataEmpty = (data: any): boolean => {
  if (!data) return true;
  if (data.length === 0) return true;
  return data?.data?.length === 0;
}

export const getDateWithMidnight = (timezone: string, date: Date = new Date()): string => {
  const momentDate = dayjs(date).tz(timezone);
  const year = momentDate.year();
  const month = String(momentDate.month() + 1).padStart(2, '0');
  const day = String(momentDate.date()).padStart(2, '0');
  return `${year}-${month}-${day} 00:00:00`;
}

export const getFormattedDateTime = (timezone: string, date: Date = new Date()): string => {
  const momentDate = dayjs(date).tz(timezone);
  const year = momentDate.year();
  const month = String(momentDate.month() + 1).padStart(2, '0');
  const day = String(momentDate.date()).padStart(2, '0');
  const hours = String(momentDate.hour()).padStart(2, '0');
  const minutes = String(momentDate.minute()).padStart(2, '0');
  const seconds = String(momentDate.second()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export const adjustDateRangeWithCurrentTime = (dateRange: string[], timezone: string): [string, string] => {
  const startDate = dateRange[0];
  const currentMoment = dayjs().tz(timezone);

  const formattedCurrentTime = currentMoment.format('YYYY-MM-DD HH:mm:ss');

  return [startDate, formattedCurrentTime];
};

export const getStartAndEndOfPreviousYear = (timezone: string) => {
  const today = dayjs().tz(timezone);
  const startOfYear = today.clone().subtract(1, 'year').startOf('year').toDate();
  const endOfYear = today.clone().subtract(1, 'year').endOf('year').toDate();

  return { startOfYear, endOfYear };
}

const MONTH_ABBREVIATIONS: Record<string, string> = {
  january: 'Jan', february: 'Feb', march: 'Mar', april: 'Apr', may: 'May', june: 'Jun',
  july: 'Jul', august: 'Aug', september: 'Sep', october: 'Oct', november: 'Nov', december: 'Dec',
};

export const getMonthAbbreviation = (fullMonthName: string) => {
  return MONTH_ABBREVIATIONS[fullMonthName.toLowerCase()] ?? fullMonthName;
};

export const formatMetricValue = (metricName: string, value: number): string | number => {
  const formatters: Record<string, (v: number) => string | number> = {
    'Add To Cart Rate': v => v.toFixed(3) + '%',
    'Interactions Conversion Rate': v => v.toFixed(3) + '%',
    'Click Through Rate': v => v.toFixed(3) + '%',
    'Order Conversion Rate': v => v.toFixed(3) + '%',
    'Order Value': v => formatCurrency(parseFloat(v.toFixed(2))),
    'Average Order Value': v => formatCurrency(parseFloat(v.toFixed(2))),
    'Revenue Generated': v => formatCurrency(parseFloat(v.toFixed(2))),
    'Average Line Items Per Order': v => v.toFixed(2)
  };
  return (formatters[metricName] || (v => v))(value);
};

export const sortGraphDataByMetricOrder = (graphData: TGraphData[], metricOrder: string[]): TGraphData[] => {
  if (!graphData.length || !metricOrder.length) {
    return graphData;
  }
  return [...graphData].sort((a, b) => {
    const indexA = metricOrder.indexOf(a.metric_name);
    const indexB = metricOrder.indexOf(b.metric_name);
    // If metric not found in order, put it at the end
    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
  });
};
